import type { Auth0ProviderOptions } from "@auth0/auth0-react";
import { Auth0Provider, useAuth0 } from "@auth0/auth0-react";
import {
  CONFIG_PERMISSIONS_ACCESS_BY_APP_ANY,
  ConfigApp,
} from "@package/ui-config";
import { jwtDecode } from "jwt-decode";
import type { PropsWithChildren } from "react";
import { useEffect, useMemo } from "react";
import {
  AUTH_REMAINING_SEC_REFRESH,
  AUTH_TOKEN_VALIDITY_CHECK_INTERVAL_MS,
} from "../../constants/contants";
import { type TokenDecoded, useAuthState } from "../../context/context-auth";
import { AuthStateProvider } from "../../context/context-auth-provider";
import { Login } from "../Login/Login";

type AuthenticationContentProps = PropsWithChildren<{
  redirectUri?: string;
}>;

function AuthenticationContent({
  redirectUri,
  children,
}: AuthenticationContentProps) {
  const { tokenDecoded, update } = useAuthState();
  const {
    error,
    isAuthenticated,
    isLoading,
    user,
    getAccessTokenSilently,
    loginWithRedirect,
  } = useAuth0();

  useEffect(
    function effectEnsureTokenValid() {
      const interval = setInterval(() => {
        if (tokenDecoded) {
          const now = new Date().getTime() / 1000;
          const remaining = tokenDecoded?.exp ?? 0 - now;

          if (remaining < AUTH_REMAINING_SEC_REFRESH) {
            loginWithRedirect({
              appState: {
                returnTo: redirectUri,
              },
            });
          }
        }
      }, AUTH_TOKEN_VALIDITY_CHECK_INTERVAL_MS);

      return () => {
        clearInterval(interval);
      };
    },
    [loginWithRedirect, redirectUri, tokenDecoded],
  );

  useEffect(
    function effectFetchToken() {
      const fetchToken = async () => {
        if (isAuthenticated) {
          const accessToken = await getAccessTokenSilently();
          if (accessToken) {
            const tokenDecoded: TokenDecoded = jwtDecode(accessToken);

            update((draft) => {
              draft.token = accessToken;
              draft.tokenDecoded = tokenDecoded;
              draft.authorizedApps = Object.values(ConfigApp).filter((a) =>
                tokenDecoded?.["https://v1.panoscape.app/permissions"]?.some(
                  (p) => CONFIG_PERMISSIONS_ACCESS_BY_APP_ANY[a].includes(p),
                ),
              );
            });
          }
        }
      };

      fetchToken();
    },
    [getAccessTokenSilently, isAuthenticated, update],
  );

  useEffect(
    function effectLoginWithRedirect() {
      if (isAuthenticated || isLoading) {
        return;
      }

      loginWithRedirect({
        appState: {
          returnTo: redirectUri,
        },
      });
    },
    [isAuthenticated, isLoading, redirectUri, loginWithRedirect],
  );

  useEffect(
    function effectSetUser() {
      update((draft) => {
        draft.user = user;
      });
    },
    [update, user],
  );

  if (isAuthenticated) {
    return <>{children}</>;
  }

  return <Login error={error?.message} />;
}

export function Authentication({
  authorizationParams,
  clientId,
  domain,
  children,
}: PropsWithChildren<Auth0ProviderOptions>) {
  const callback = useMemo<string>(
    () =>
      `${authorizationParams?.redirect_uri}?returnTo=${encodeURIComponent(window.location.href.replace(window.location.origin, ""))}`,
    [authorizationParams?.redirect_uri],
  );

  return (
    <AuthStateProvider>
      <Auth0Provider
        authorizationParams={{
          ...authorizationParams,
          redirect_uri: callback,
        }}
        clientId={clientId}
        domain={domain}
        useRefreshTokens
      >
        <AuthenticationContent redirectUri={callback}>
          {children}
        </AuthenticationContent>
      </Auth0Provider>
    </AuthStateProvider>
  );
}
